import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Play, Pause, RotateCcw, Maximize, Minimize, SkipForward, SkipBack, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VideoPlayer = ({ exercise, onVideoComplete, isCompleted, onNextVideo, hasNextVideo, onPreviousVideo, hasPreviousVideo, layoutMode = "horizontal" }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasStarted, setHasStarted] = useState(false); // Nuevo estado para saber si el video ha iniciado
  const [isFullscreen, setIsFullscreen] = useState(false); // Estado para pantalla completa
  const [volume, setVolume] = useState(1); // Volumen (0-1)
  const [isMuted, setIsMuted] = useState(false); // Estado de silencio
  const [showVolumeSlider, setShowVolumeSlider] = useState(false); // Mostrar/ocultar slider
  const [previousVolume, setPreviousVolume] = useState(1); // Guardar volumen antes de silenciar
  const hasCalledComplete = useRef(false); // Ref para rastrear si ya se llamó onVideoComplete
  const shouldAutoPlay = useRef(false); // Ref para trackear si debe auto-reproducir
  const volumeTimeoutRef = useRef(null); // Timeout para ocultar slider


  // Reset video when exercise changes
  useEffect(() => {
    const video = videoRef.current;
    if (video && exercise?.idStorage) {
      video.pause();
      setIsPlaying(false);
      setProgress(0);
      setHasStarted(false); // Reset hasStarted cuando cambia el video
      hasCalledComplete.current = false; // Reset la bandera cuando cambia el ejercicio
      video.currentTime = 0;
      video.load(); // Force reload of the video element

      // Si debe auto-reproducir, iniciar el video después de cargar
      if (shouldAutoPlay.current) {
        setTimeout(() => {
          video.play().then(() => {
            setIsPlaying(true);
            setHasStarted(true);
          }).catch(err => {
            console.error("Error al auto-reproducir:", err);
          });
          shouldAutoPlay.current = false; // Reset la bandera
        }, 300); // Pequeño delay para asegurar que el video esté listo
      }
    }
  }, [exercise?.idStorage]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateProgress = () => {
      if (video.duration) {
        const progressPercent = (video.currentTime / video.duration) * 100;
        setProgress(progressPercent);

        if (progressPercent >= 95 && !isCompleted && !hasCalledComplete.current) {
          hasCalledComplete.current = true;
          onVideoComplete(exercise.idVideo);
        }
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (!isCompleted && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onVideoComplete(exercise.idVideo);
      }

      // Auto-reproducir el siguiente video si existe
      if (hasNextVideo) {
        shouldAutoPlay.current = true; // Marcar que debe auto-reproducir
        setTimeout(() => {
          onNextVideo();
        }, 500); // Pequeño delay para una transición más suave
      }
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('ended', handleEnded);
    };
  }, [exercise.idVideo, onVideoComplete, isCompleted, hasNextVideo, onNextVideo]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (isPlaying) {
      video.pause();
    } else {
      video.play();
      setHasStarted(true); // Marcar que el video ha iniciado cuando se reproduce
    }
    setIsPlaying(!isPlaying);
  };

  const resetVideo = () => {
    const video = videoRef.current;
    video.currentTime = 0;
    setProgress(0);
    setHasStarted(false); // Reset hasStarted cuando se reinicia el video
    hasCalledComplete.current = false; // Reset la bandera cuando se reinicia
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    }
  };

  // Función para alternar mute/unmute
  const toggleMute = () => {
    const video = videoRef.current;
    if (isMuted) {
      // Unmute: restaurar volumen anterior
      const volumeToRestore = previousVolume > 0 ? previousVolume : 1;
      video.volume = volumeToRestore;
      setVolume(volumeToRestore);
      setIsMuted(false);
    } else {
      // Mute: guardar volumen actual y silenciar
      setPreviousVolume(volume);
      video.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  // Función para cambiar el volumen
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    const video = videoRef.current;
    video.volume = newVolume;
    setVolume(newVolume);

    // Si el volumen es 0, marcar como muted
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
      setPreviousVolume(newVolume);
    }
  };

  // Función para mostrar el slider de volumen
  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
    }
    setShowVolumeSlider(true);
  };

  // Función para ocultar el slider de volumen con delay
  const handleVolumeMouseLeave = () => {
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false);
    }, 500);
  };

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
    };
  }, []);

  // Ref para el contenedor del video
  const containerRef = useRef(null);

  // Función para manejar pantalla completa
  const toggleFullscreen = async () => {
    const container = containerRef.current;

    if (!document.fullscreenElement) {
      // Entrar a pantalla completa
      try {
        await container.requestFullscreen();
        setIsFullscreen(true);
      } catch (error) {
        console.error('Error al entrar en pantalla completa:', error);
      }
    } else {
      // Salir de pantalla completa
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (error) {
        console.error('Error al salir de pantalla completa:', error);
      }
    }
  };

  // Escuchar cambios de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      setIsFullscreen(isFullscreenNow);

      // Ocultar controles nativos del video cuando está en pantalla completa
      const video = videoRef.current;
      if (video) {
        if (isFullscreenNow) {
          video.removeAttribute('controls');
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange); // Safari
    document.addEventListener('mozfullscreenchange', handleFullscreenChange); // Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange); // IE/Edge

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <>
      {/* Estilos globales para ocultar controles del video */}
      <style>
        {`
          video::-webkit-media-controls {
            display: none !important;
          }
          video::-webkit-media-controls-enclosure {
            display: none !important;
          }
          video::-webkit-media-controls-panel {
            display: none !important;
          }
          video::-webkit-media-controls-play-button {
            display: none !important;
          }
          video::-webkit-media-controls-timeline {
            display: none !important;
          }
          video::-webkit-media-controls-current-time-display {
            display: none !important;
          }
          video::-webkit-media-controls-time-remaining-display {
            display: none !important;
          }
          video::-webkit-media-controls-mute-button {
            display: none !important;
          }
          video::-webkit-media-controls-volume-slider {
            display: none !important;
          }
          video::-webkit-media-controls-fullscreen-button {
            display: none !important;
          }
          video::-moz-media-controls {
            display: none !important;
          }
          video::--ms-media-controls {
            display: none !important;
          }

          /* Ocultar controles en pantalla completa específicamente */
          video:fullscreen::-webkit-media-controls {
            display: none !important;
          }
          video:-webkit-full-screen::-webkit-media-controls {
            display: none !important;
          }
          video:-moz-full-screen::-moz-media-controls {
            display: none !important;
          }
          video:-ms-fullscreen::--ms-media-controls {
            display: none !important;
          }

          /* Mostrar controles en hover - modo normal */
          .video-container:hover .video-overlay {
            opacity: 1 !important;
          }

          /* Mostrar controles en hover - modo pantalla completa */
          .video-container:fullscreen:hover .video-overlay,
          .video-container:-webkit-full-screen:hover .video-overlay,
          .video-container:-moz-full-screen:hover .video-overlay,
          .video-container:-ms-fullscreen:hover .video-overlay {
            opacity: 1 !important;
          }

          /* Asegurar que el contenedor en fullscreen ocupe todo el espacio */
          .video-container:fullscreen,
          .video-container:-webkit-full-screen,
          .video-container:-moz-full-screen,
          .video-container:-ms-fullscreen {
            width: 100vw;
            height: 100vh;
            background-color: #000;
          }

          /* Asegurar que el video en fullscreen ocupe todo el espacio */
          .video-container:fullscreen video,
          .video-container:-webkit-full-screen video,
          .video-container:-moz-full-screen video,
          .video-container:-ms-fullscreen video {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        `}
      </style>
      <Container className={layoutMode === "horizontal" ? "mt-2 mb-2" : "p-0"} fluid={layoutMode === "vertical"}>
        <Row className={layoutMode === "horizontal" ? "justify-content-center" : ""}>
          <Col md={layoutMode === "horizontal" ? 8 : 12} lg={layoutMode === "horizontal" ? 6 : 12}>

          {/* Título del video - Solo en modo vertical */}
          {layoutMode === "vertical" && (
            <div className="mb-3">
              <h4 className="text-white fw-bold mb-1" style={{ fontSize: '1.2rem' }}>
                {exercise?.name || t('move.exerciseLabel')}
              </h4>
              {exercise?.routineName && (
                <p className="text-white mb-0" style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  {exercise.routineName}
                </p>
              )}
            </div>
          )}

          {/* Contenedor del video */}
          <div
            ref={containerRef}
            className="video-container bg-white shadow-lg position-relative"
            style={{
              borderRadius: '24px',
              overflow: 'hidden',
              height: layoutMode === "vertical" ? 'calc(100vh - 250px)' : '400px',
              maxHeight: layoutMode === "vertical" ? '600px' : '400px',
              marginBottom: layoutMode === "horizontal" ? '0.5rem' : '1rem'
            }}
          > 
            {/* Video */}
            <video
              ref={videoRef}
              className="w-100 h-100"
              style={{
                objectFit: 'contain',
                display: exercise?.idStorage ? 'block' : 'none',
                backgroundColor: '#000'
              }}
              onContextMenu={(e) => e.preventDefault()}
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              playsInline
            >
              {exercise?.idStorage && (
                <source src={exercise.idStorage} type="video/mp4" />
              )}
              Tu navegador no soporta el elemento video.
            </video>
            
            {/* Overlay de controles hover */}
            <div 
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center video-overlay"
              style={{
                background: 'rgba(0,0,0,0.2)',
                opacity: 0,
                transition: 'opacity 0.3s'
              }}
            >
              <div className="d-flex gap-3">
                {hasPreviousVideo && (
                  <Button
                    variant="light"
                    className="rounded-circle p-3 shadow"
                    onClick={onPreviousVideo}
                    style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                  >
                    <SkipBack size={28} />
                  </Button>
                )}

                <Button
                  variant="light"
                  className="rounded-circle p-3 shadow"
                  onClick={togglePlay}
                  style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                >
                  {isPlaying ? <Pause size={28} /> : <Play size={28} />}
                </Button>

                <Button
                  variant="light"
                  className="rounded-circle p-3 shadow"
                  onClick={resetVideo}
                  style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                >
                  <RotateCcw size={28} />
                </Button>

                <Button
                  variant="light"
                  className="rounded-circle p-3 shadow"
                  onClick={toggleFullscreen}
                  style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                >
                  {isFullscreen ? <Minimize size={28} /> : <Maximize size={28} />}
                </Button>

                {/* Control de volumen */}
                <div
                  className="position-relative d-flex align-items-center"
                  onMouseEnter={handleVolumeMouseEnter}
                  onMouseLeave={handleVolumeMouseLeave}
                >
                  <Button
                    variant="light"
                    className="rounded-circle p-3 shadow"
                    onClick={toggleMute}
                    style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                  >
                    {isMuted || volume === 0 ? <VolumeX size={28} /> : <Volume2 size={28} />}
                  </Button>

                  {/* Slider de volumen - Solo el control sin fondo */}
                  {showVolumeSlider && (
                    <div
                      className="position-absolute"
                      style={{
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '10px',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100px'
                      }}
                    >
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                        style={{
                          width: '80px',
                          height: '6px',
                          WebkitAppearance: 'none',
                          appearance: 'none',
                          background: `linear-gradient(to right, #fff 0%, #fff ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%, rgba(255,255,255,0.3) 100%)`,
                          borderRadius: '3px',
                          outline: 'none',
                          transform: 'rotate(-90deg)',
                          transformOrigin: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      />
                    </div>
                  )}
                </div>

                {hasNextVideo && (
                  <Button
                    variant="light"
                    className="rounded-circle p-3 shadow"
                    onClick={onNextVideo}
                    style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                  >
                    <SkipForward size={28} />
                  </Button>
                )}
              </div>
            </div>

            {/* Estilos adicionales para el slider de volumen */}
            <style>
              {`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 16px;
                  height: 16px;
                  background: #fff;
                  cursor: pointer;
                  border-radius: 50%;
                  border: none;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.4);
                }

                input[type="range"]::-moz-range-thumb {
                  width: 16px;
                  height: 16px;
                  background: #fff;
                  cursor: pointer;
                  border-radius: 50%;
                  border: none;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.4);
                }

                input[type="range"]::-webkit-slider-thumb:hover {
                  transform: scale(1.15);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.5);
                }

                input[type="range"]::-moz-range-thumb:hover {
                  transform: scale(1.15);
                  box-shadow: 0 2px 6px rgba(0,0,0,0.5);
                }
              `}
            </style>
          </div>

          {/* Botones de control externos - Solo en modo horizontal */}
          {layoutMode === "horizontal" && (
            <Row className="px-3 mt-1">
              <Col className="d-flex justify-content-between">
                <Button
                  variant="link"
                  className="text-white text-decoration-none fw-medium"
                  style={{ fontSize: '1rem', padding: '0.25rem 0' }}
                  onClick={togglePlay}
                >
                  {isPlaying ? t('move.pauseButton') : (hasStarted ? t('move.resumeButton') : t('move.playButton'))}
                </Button>
                <Button
                  variant="link"
                  className="text-white text-decoration-none fw-medium"
                  style={{ fontSize: '1rem', padding: '0.25rem 0' }}
                  onClick={resetVideo}
                >
                  {t('move.restartButton')}
                </Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default VideoPlayer;